import React, { useState, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../utils/api';

// ── Shared types ──────────────────────────────────────────────────────────────

interface OnboardingCandidate {
  userId: string;
  userEmail: string;
  userName: string;
  firstName: string;
  cycleCount: number;
  primaryCycleName: string;
  daysSinceRegistration: number;
  createdAt: string;
  lastSentAt?: string;
}

interface CycleSetupCandidate {
  userId: string;
  userEmail: string;
  userName: string;
  firstName: string;
  licenseCount: number;
  professionID: string;
  stateNames: string[];
  lastSentAt?: string;
}

interface AtRiskCandidate {
  userId: string;
  userEmail: string;
  userName: string;
  firstName: string;
  cycleName: string;
  daysRemaining: number;
  compliancePercentage: number;
  isMet: boolean;
}

interface PreviewResult<T> {
  candidates: T[];
  sampleHtml: string;
  totalCount: number;
}

interface SendResult {
  sent: number;
  failed: number;
  errors: string[];
}

interface CronConfigData {
  jobType: string;
  enabled: boolean;
  frequencyDays: number;
  minDaysOld: number;
  daysWindow?: number;
  excludedUserIds?: string[];
  lastRunAt?: string;
  lastRunCount?: number;
  lastRunErrors?: string[];
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'white', borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden',
};

const BTN_PRIMARY: React.CSSProperties = {
  padding: '10px 24px', background: '#0F1F3D', color: 'white',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

const BTN_CONFIRM: React.CSSProperties = {
  padding: '8px 14px', background: '#DC2626', color: 'white',
  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
};

const BTN_CANCEL: React.CSSProperties = {
  padding: '8px 12px', background: '#F3F4F6', color: '#374151',
  border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
};

function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Send result banner ────────────────────────────────────────────────────────

function SendResultBanner({ result }: { result: SendResult }) {
  return (
    <div style={{
      background: result.failed === 0 ? '#F0FDF4' : '#FFFBEB',
      border: `1px solid ${result.failed === 0 ? '#BBF7D0' : '#FCD34D'}`,
      borderRadius: 10, padding: '20px 24px', marginBottom: 24,
    }}>
      <h3 style={{ margin: '0 0 8px', color: '#065F46', fontSize: 16, fontWeight: 700 }}>✅ Emails sent</h3>
      <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>
        Sent: <strong>{result.sent}</strong> &nbsp;·&nbsp; Failed: <strong>{result.failed}</strong>
      </p>
      {result.errors.length > 0 && (
        <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13, color: '#92400E' }}>
          {result.errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

// ── CronPanel ─────────────────────────────────────────────────────────────────

function CronPanel({ jobType, showMinDays = true }: { jobType: string; showMinDays?: boolean }) {
  const [config, setConfig] = useState<CronConfigData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  React.useEffect(() => {
    api.get<Record<string, CronConfigData>>('/api/cron-config')
      .then(({ data }) => setConfig(data[jobType] ?? null))
      .catch(() => {});
  }, [jobType]);

  if (!config) return null;

  const nextRunAt = config.lastRunAt
    ? new Date(new Date(config.lastRunAt).getTime() + config.frequencyDays * 86400000)
    : null;
  const isOverdue = nextRunAt ? nextRunAt < new Date() : false;

  const save = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const { data } = await api.patch<CronConfigData>(`/api/cron-config/${jobType}`, {
        enabled: config.enabled,
        frequencyDays: config.frequencyDays,
        minDaysOld: config.minDaysOld,
      });
      setConfig(data);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err: any) {
      console.error('[CronPanel save]', err?.response?.status, err?.response?.data, err);
      const msg = err?.response?.data?.error || err?.message || 'Save failed';
      setSaveMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...CARD, padding: '20px 24px', marginTop: 28, borderLeft: '4px solid #0F1F3D' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Automation
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>Runs nightly at 3 AM ET — sends only when due</p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: config.enabled ? '#065F46' : '#9CA3AF' }}>
            {config.enabled ? 'ON' : 'OFF'}
          </span>
          <div
            role="switch"
            aria-checked={config.enabled}
            onClick={() => setConfig(c => c ? { ...c, enabled: !c.enabled } : c)}
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: config.enabled ? '#0F1F3D' : '#D1D5DB',
              position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 3, left: config.enabled ? 23 : 3,
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            }} />
          </div>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Run every (days)
          </label>
          <input
            type="number" min={1} max={365} value={config.frequencyDays}
            onChange={e => setConfig(c => c ? { ...c, frequencyDays: Number(e.target.value) } : c)}
            style={{ width: 80, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, color: '#111827' }}
          />
        </div>
        {showMinDays && (
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Min account age (days)
            </label>
            <input
              type="number" min={0} max={365} value={config.minDaysOld}
              onChange={e => setConfig(c => c ? { ...c, minDaysOld: Number(e.target.value) } : c)}
              style={{ width: 80, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, color: '#111827' }}
            />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 32, marginBottom: 16, fontSize: 12, color: '#6B7280', flexWrap: 'wrap' }}>
        <span>
          <strong style={{ color: '#374151' }}>Last run: </strong>
          {config.lastRunAt
            ? <>
                {new Date(config.lastRunAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {config.lastRunCount !== undefined && <> · <strong style={{ color: '#374151' }}>{config.lastRunCount} sent</strong></>}
              </>
            : 'Never'}
          {config.lastRunErrors && config.lastRunErrors.length > 0 && (
            <span style={{ marginLeft: 6, color: '#DC2626' }}>({config.lastRunErrors.length} error{config.lastRunErrors.length !== 1 ? 's' : ''})</span>
          )}
        </span>
        <span style={{ color: isOverdue && config.enabled ? '#DC2626' : '#6B7280' }}>
          <strong style={{ color: isOverdue && config.enabled ? '#DC2626' : '#374151' }}>Next run: </strong>
          {config.enabled
            ? nextRunAt
              ? isOverdue
                ? 'Overdue — runs tonight at 3 AM'
                : nextRunAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : 'Tonight at 3 AM (first run)'
            : 'Disabled'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving} style={{ ...BTN_PRIMARY, padding: '8px 20px', fontSize: 13, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saveMsg && (
          <span style={{ fontSize: 13, fontWeight: 600, color: saveMsg === 'Saved' ? '#065F46' : '#DC2626' }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Onboarding tab ────────────────────────────────────────────────────────────

function OnboardingTab() {
  const [minDaysOld, setMinDaysOld] = useState(3);
  const [preview, setPreview] = useState<PreviewResult<OnboardingCandidate> | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setSendResult(null);
    setPreview(null);
    setPreviewHtml('');
    setSelectedUserId(null);
    setCheckedIds(new Set());
    try {
      const { data } = await api.get<PreviewResult<OnboardingCandidate>>(`/api/onboarding/preview?minDaysOld=${minDaysOld}`);
      setPreview(data);
      if (data.sampleHtml) {
        setPreviewHtml(data.sampleHtml);
        setSelectedUserId(data.candidates[0]?.userId ?? null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [minDaysOld]);

  const loadUserPreview = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      const { data } = await api.get<{ html: string }>(`/api/onboarding/preview-email/${userId}?minDaysOld=0`);
      setPreviewHtml(data.html);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleCheck = (userId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    const allIds = new Set(preview.candidates.map(c => c.userId));
    setCheckedIds(prev => prev.size === allIds.size ? new Set() : allIds);
  };

  const sendEmails = async () => {
    if (!preview || preview.totalCount === 0) return;
    setSending(true);
    setError('');
    setConfirmSend(false);
    try {
      const userIds = checkedIds.size > 0 ? Array.from(checkedIds) : undefined;
      const { data } = await api.post<SendResult>('/api/onboarding/send', { minDaysOld, userIds });
      setSendResult(data);
      setPreview(null);
      setPreviewHtml('');
      setCheckedIds(new Set());
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendCount = checkedIds.size > 0 ? checkedIds.size : (preview?.totalCount ?? 0);

  return (
    <>
      <p style={{ margin: '0 0 24px', color: '#6B7280', fontSize: 14 }}>
        Send a step-by-step guide to users who registered but haven't added any classes yet.
      </p>

      <div style={{ ...CARD, padding: '24px 28px', marginBottom: 24, display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Minimum account age (days)
          </label>
          <input
            type="number" min={0} max={365} value={minDaysOld}
            onChange={e => setMinDaysOld(Number(e.target.value))}
            style={{ width: 90, padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, color: '#111827' }}
          />
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9CA3AF' }}>
            Only target accounts created ≥ this many days ago
          </p>
        </div>
        <button onClick={loadPreview} disabled={loading} style={{ ...BTN_PRIMARY, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Loading…' : 'Load Preview'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#991B1B', fontSize: 14 }}>
          {error}
        </div>
      )}

      {sendResult && <SendResultBanner result={sendResult} />}

      {preview && (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Eligible users</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {checkedIds.size > 0
                    ? `${checkedIds.size} of ${preview.totalCount} selected`
                    : `${preview.totalCount} user${preview.totalCount !== 1 ? 's' : ''} — none selected (will send all)`}
                </p>
              </div>
              {preview.totalCount > 0 && !confirmSend && (
                <button onClick={() => setConfirmSend(true)} style={{ ...BTN_PRIMARY, padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                  Send {checkedIds.size > 0 ? `(${checkedIds.size})` : 'All'}
                </button>
              )}
              {confirmSend && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={sendEmails} disabled={sending} style={{ ...BTN_CONFIRM, cursor: sending ? 'not-allowed' : 'pointer' }}>
                    {sending ? 'Sending…' : `Confirm (${sendCount})`}
                  </button>
                  <button onClick={() => setConfirmSend(false)} style={BTN_CANCEL}>Cancel</button>
                </div>
              )}
            </div>
            {preview.totalCount > 0 && (
              <div onClick={toggleAll} style={{ padding: '8px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" readOnly checked={checkedIds.size === preview.totalCount} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D' }} />
                <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
                  {checkedIds.size === preview.totalCount ? 'Deselect all' : 'Select all'}
                </span>
              </div>
            )}
            {preview.totalCount === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6B7280', fontSize: 14 }}>
                No eligible users found.
              </div>
            ) : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {preview.candidates.map(c => (
                  <div key={c.userId} style={{
                    padding: '12px 16px', borderBottom: '1px solid #F3F4F6',
                    background: selectedUserId === c.userId ? '#EFF6FF' : checkedIds.has(c.userId) ? '#F8FAFF' : 'white',
                    borderLeft: `3px solid ${selectedUserId === c.userId ? '#0F1F3D' : checkedIds.has(c.userId) ? '#93C5FD' : 'transparent'}`,
                    display: 'flex', alignItems: 'flex-start', gap: 10, transition: 'background 0.1s',
                  }}>
                    <input type="checkbox" checked={checkedIds.has(c.userId)}
                      onChange={() => { toggleCheck(c.userId); loadUserPreview(c.userId); }}
                      onClick={e => e.stopPropagation()}
                      style={{ marginTop: 3, width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D', flexShrink: 0 }} />
                    <div onClick={() => loadUserPreview(c.userId)} style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.userName}</p>
                      <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.userEmail}</p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>{c.cycleCount} cycle{c.cycleCount !== 1 ? 's' : ''}</span>
                        <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>{c.daysSinceRegistration}d old</span>
                        {c.lastSentAt && (
                          <span style={{ fontSize: 11, background: '#FFF7ED', color: '#92400E', padding: '2px 7px', borderRadius: 10 }}>
                            Sent {fmtDate(c.lastSentAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Email preview</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                {selectedUserId
                  ? <>Personalized for: <strong>{preview.candidates.find(c => c.userId === selectedUserId)?.userEmail}</strong> — each recipient gets their own version</>
                  : 'Click or check a user on the left to preview their personalized email'}
              </p>
            </div>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} title="Email Preview" style={{ width: '100%', height: 680, border: 'none', display: 'block' }} sandbox="allow-same-origin" />
            ) : (
              <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 14 }}>
                No email selected
              </div>
            )}
          </div>
        </div>
      )}

      <CronPanel jobType="onboarding" showMinDays />
    </>
  );
}

// ── Cycle Setup tab ───────────────────────────────────────────────────────────

function CycleSetupTab() {
  const [preview, setPreview] = useState<PreviewResult<CycleSetupCandidate> | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setSendResult(null);
    setPreview(null);
    setPreviewHtml('');
    setSelectedUserId(null);
    setCheckedIds(new Set());
    try {
      const { data } = await api.get<PreviewResult<CycleSetupCandidate>>('/api/onboarding/cycle-setup/preview');
      setPreview(data);
      if (data.candidates.length > 0) {
        setSelectedUserId(data.candidates[0].userId);
        setPreviewHtml(data.sampleHtml);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserPreview = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      const { data } = await api.get<{ html: string }>(`/api/onboarding/cycle-setup/preview-email/${userId}`);
      setPreviewHtml(data.html);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleCheck = (userId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    const allIds = new Set(preview.candidates.map(c => c.userId));
    setCheckedIds(prev => prev.size === allIds.size ? new Set() : allIds);
  };

  const sendEmails = async () => {
    if (!preview || preview.totalCount === 0) return;
    setSending(true);
    setError('');
    setConfirmSend(false);
    try {
      const userIds = checkedIds.size > 0 ? Array.from(checkedIds) : undefined;
      const { data } = await api.post<SendResult>('/api/onboarding/cycle-setup/send-bulk', { userIds });
      setSendResult(data);
      setPreview(null);
      setPreviewHtml('');
      setCheckedIds(new Set());
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendCount = checkedIds.size > 0 ? checkedIds.size : (preview?.totalCount ?? 0);

  return (
    <>
      <p style={{ margin: '0 0 24px', color: '#6B7280', fontSize: 14 }}>
        Send a cycle-setup guide to users who have licenses or classes but haven't created a renewal cycle yet.
      </p>

      <div style={{ marginBottom: 24 }}>
        <button onClick={loadPreview} disabled={loading} style={{ ...BTN_PRIMARY, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Loading…' : 'Load Preview'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#991B1B', fontSize: 14 }}>
          {error}
        </div>
      )}

      {sendResult && <SendResultBanner result={sendResult} />}

      {preview && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>No-cycle users</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {checkedIds.size > 0
                    ? `${checkedIds.size} of ${preview.totalCount} selected`
                    : `${preview.totalCount} user${preview.totalCount !== 1 ? 's' : ''} — none selected (will send all)`}
                </p>
              </div>
              {preview.totalCount > 0 && !confirmSend && (
                <button onClick={() => setConfirmSend(true)} style={{ ...BTN_PRIMARY, padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                  Send {checkedIds.size > 0 ? `(${checkedIds.size})` : 'All'}
                </button>
              )}
              {confirmSend && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={sendEmails} disabled={sending} style={{ ...BTN_CONFIRM, cursor: sending ? 'not-allowed' : 'pointer' }}>
                    {sending ? 'Sending…' : `Confirm (${sendCount})`}
                  </button>
                  <button onClick={() => setConfirmSend(false)} style={BTN_CANCEL}>Cancel</button>
                </div>
              )}
            </div>
            {preview.totalCount > 0 && (
              <div onClick={toggleAll} style={{ padding: '8px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" readOnly checked={checkedIds.size === preview.totalCount} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D' }} />
                <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
                  {checkedIds.size === preview.totalCount ? 'Deselect all' : 'Select all'}
                </span>
              </div>
            )}
            {preview.totalCount === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6B7280', fontSize: 14 }}>
                No eligible users found.
                <br />
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>All users with licenses have at least one cycle set up.</span>
              </div>
            ) : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {preview.candidates.map(c => (
                  <div key={c.userId} style={{
                    padding: '12px 16px', borderBottom: '1px solid #F3F4F6',
                    background: selectedUserId === c.userId ? '#EFF6FF' : checkedIds.has(c.userId) ? '#F8FAFF' : 'white',
                    borderLeft: `3px solid ${selectedUserId === c.userId ? '#0F1F3D' : checkedIds.has(c.userId) ? '#93C5FD' : 'transparent'}`,
                    display: 'flex', alignItems: 'flex-start', gap: 10, transition: 'background 0.1s',
                  }}>
                    <input type="checkbox" checked={checkedIds.has(c.userId)}
                      onChange={() => { toggleCheck(c.userId); loadUserPreview(c.userId); }}
                      onClick={e => e.stopPropagation()}
                      style={{ marginTop: 3, width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D', flexShrink: 0 }} />
                    <div onClick={() => loadUserPreview(c.userId)} style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.userName}</p>
                      <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.userEmail}</p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {c.professionID && (
                          <span style={{ fontSize: 11, background: '#EFF6FF', color: '#1D4ED8', padding: '2px 7px', borderRadius: 10 }}>{c.professionID}</span>
                        )}
                        {c.stateNames.length > 0 && (
                          <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>{c.stateNames.join(', ')}</span>
                        )}
                        <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>
                          {c.licenseCount} license{c.licenseCount !== 1 ? 's' : ''}
                        </span>
                        {c.lastSentAt && (
                          <span style={{ fontSize: 11, background: '#FFF7ED', color: '#92400E', padding: '2px 7px', borderRadius: 10 }}>
                            Sent {fmtDate(c.lastSentAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Email preview</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                {selectedUserId
                  ? <>Personalized for: <strong>{preview.candidates.find(c => c.userId === selectedUserId)?.userEmail}</strong> — each recipient gets their own version</>
                  : 'Click or check a user on the left to preview their personalized email'}
              </p>
            </div>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} title="Cycle Setup Email Preview" style={{ width: '100%', height: 680, border: 'none', display: 'block' }} sandbox="allow-same-origin" />
            ) : (
              <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 14 }}>
                No email selected
              </div>
            )}
          </div>
        </div>
      )}

      <CronPanel jobType="cycle-setup" showMinDays />
    </>
  );
}

// ── At-Risk Reminders tab ─────────────────────────────────────────────────────

interface BatchSummary {
  _id: string;
  status: 'pending' | 'sent' | 'cancelled';
  scheduledSendAt: string;
  candidates: { userId: string }[];
  sentAt?: string;
  sentCount?: number;
  failedCount?: number;
  cancelledAt?: string;
  createdAt: string;
}

function AtRiskAutomationPanel() {
  const [config, setConfig] = useState<CronConfigData | null>(null);
  const [roster, setRoster] = useState<AtRiskCandidate[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get<Record<string, CronConfigData>>('/api/cron-config');
      const cfg = data['at-risk'];
      setConfig(cfg ?? null);
      if (cfg) await loadRoster(cfg.daysWindow ?? 30);
    } catch { /* non-fatal */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoster = async (daysWindow: number) => {
    setRosterLoading(true);
    try {
      const { data } = await api.get<PreviewResult<AtRiskCandidate>>(`/api/notifications/at-risk/preview?daysWindow=${daysWindow}`);
      setRoster(data.candidates);
    } catch { /* non-fatal */ }
    setRosterLoading(false);
  };

  React.useEffect(() => { loadConfig(); }, [loadConfig]);

  if (!config) return null;

  const excluded = new Set(config.excludedUserIds ?? []);
  const daysWindow = config.daysWindow ?? 30;

  const toggleExclusion = (userId: string) => {
    const next = new Set(excluded);
    next.has(userId) ? next.delete(userId) : next.add(userId);
    setConfig(c => c ? { ...c, excludedUserIds: Array.from(next) } : c);
  };

  const setDaysWindow = (d: number) => {
    setConfig(c => c ? { ...c, daysWindow: d } : c);
    loadRoster(d);
  };

  const save = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const { data } = await api.patch<CronConfigData>('/api/cron-config/at-risk', {
        enabled: config.enabled,
        daysWindow: config.daysWindow,
        frequencyDays: config.frequencyDays ?? 2,
        excludedUserIds: config.excludedUserIds ?? [],
      });
      setConfig(data);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err: any) {
      console.error('[AtRiskAutomationPanel save]', err?.response?.status, err?.response?.data, err);
      setSaveMsg(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const urgencyColor = (days: number) => days <= 7 ? '#EF4444' : days <= 14 ? '#F59E0B' : '#6B7280';
  const includedCount = roster.filter(c => !excluded.has(c.userId)).length;

  return (
    <div style={{ ...CARD, padding: '20px 24px', marginTop: 28, borderLeft: '4px solid #0F1F3D' }}>
      {/* Toggle row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Automation</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
            When ON — batches at 5 PM ET, sends at 8 AM next morning
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: config.enabled ? '#065F46' : '#9CA3AF' }}>
            {config.enabled ? 'ON' : 'OFF'}
          </span>
          <div
            role="switch" aria-checked={config.enabled}
            onClick={() => setConfig(c => c ? { ...c, enabled: !c.enabled } : c)}
            style={{ width: 44, height: 24, borderRadius: 12, background: config.enabled ? '#0F1F3D' : '#D1D5DB', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' }}
          >
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: config.enabled ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
          </div>
        </label>
      </div>

      {/* Days window */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#374151' }}>
          Notify users with cycles ending within
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          {[30, 45, 60].map(d => (
            <button key={d} onClick={() => setDaysWindow(d)} style={{
              padding: '6px 16px', border: `2px solid ${daysWindow === d ? '#0F1F3D' : '#D1D5DB'}`,
              borderRadius: 6, background: daysWindow === d ? '#0F1F3D' : 'white',
              color: daysWindow === d ? 'white' : '#374151',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
            }}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {/* Frequency */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#374151' }}>
          Send every (days)
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number" min={1} max={30}
            value={config.frequencyDays ?? 2}
            onChange={e => setConfig(c => c ? { ...c, frequencyDays: Math.max(1, Number(e.target.value)) } : c)}
            style={{ width: 64, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, color: '#111827' }}
          />
          <span style={{ fontSize: 12, color: '#6B7280' }}>days between reminders (from window start until cycle end)</span>
        </div>
      </div>

      {/* Last run */}
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 20 }}>
        <strong style={{ color: '#374151' }}>Last run: </strong>
        {config.lastRunAt
          ? <>{new Date(config.lastRunAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}{config.lastRunCount !== undefined && <> · <strong style={{ color: '#374151' }}>{config.lastRunCount} sent</strong></>}</>
          : 'Never'}
      </div>

      {/* Automation roster */}
      <div style={{ marginBottom: 16 }}>
        <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Automation roster
        </p>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6B7280' }}>
          {rosterLoading
            ? 'Loading…'
            : roster.length === 0
              ? `No users with cycles ending within ${daysWindow} days`
              : `${includedCount} of ${roster.length} user${roster.length !== 1 ? 's' : ''} will receive the next automated batch — uncheck to exclude`}
        </p>
        {roster.length > 0 && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', maxHeight: 340, overflowY: 'auto' }}>
            {roster.map(c => {
              const isExcluded = excluded.has(c.userId);
              return (
                <div key={c.userId} style={{
                  padding: '10px 14px', borderBottom: '1px solid #F3F4F6',
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: isExcluded ? '#FAFAFA' : 'white',
                  opacity: isExcluded ? 0.6 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={!isExcluded}
                    onChange={() => toggleExclusion(c.userId)}
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 1px', fontSize: 13, fontWeight: 600, color: '#111827' }}>{c.userName}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.userEmail}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>{c.cycleName}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: urgencyColor(c.daysRemaining) }}>{c.daysRemaining}d left</span>
                    <span style={{ fontSize: 11, color: c.isMet ? '#065F46' : '#991B1B', background: c.isMet ? '#D1FAE5' : '#FEE2E2', padding: '2px 7px', borderRadius: 10 }}>
                      {c.isMet ? '✓' : `${c.compliancePercentage.toFixed(0)}%`}
                    </span>
                    {isExcluded && <span style={{ fontSize: 11, background: '#FEE2E2', color: '#991B1B', padding: '2px 7px', borderRadius: 10 }}>Excluded</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving} style={{ ...BTN_PRIMARY, padding: '8px 20px', fontSize: 13, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saveMsg && (
          <span style={{ fontSize: 13, fontWeight: 600, color: saveMsg === 'Saved' ? '#065F46' : '#DC2626' }}>{saveMsg}</span>
        )}
      </div>
    </div>
  );
}

function AtRiskTab() {
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [preview, setPreview] = useState<PreviewResult<AtRiskCandidate> | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get<BatchSummary[]>('/api/notifications/at-risk/batches');
      setBatches(data);
    } catch { /* non-fatal */ }
  }, []);

  const cancelBatch = async (id: string) => {
    if (!window.confirm('Cancel this scheduled batch? The emails will not be sent automatically.')) return;
    try {
      await api.delete(`/api/notifications/at-risk/batches/${id}`);
      await loadBatches();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  React.useEffect(() => { loadBatches(); }, [loadBatches]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setSendResult(null);
    setPreview(null);
    setPreviewHtml('');
    setSelectedUserId(null);
    setCheckedIds(new Set());
    try {
      const { data } = await api.get<PreviewResult<AtRiskCandidate>>('/api/notifications/at-risk/preview');
      setPreview(data);
      if (data.sampleHtml) {
        setPreviewHtml(data.sampleHtml);
        setSelectedUserId(data.candidates[0]?.userId ?? null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserPreview = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      const { data } = await api.get<{ html: string; candidate: AtRiskCandidate }>(`/api/notifications/at-risk/preview-email/${userId}`);
      setPreviewHtml(data.html);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleCheck = (userId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    const allIds = new Set(preview.candidates.map(c => c.userId));
    setCheckedIds(prev => prev.size === allIds.size ? new Set() : allIds);
  };

  const sendEmails = async () => {
    if (!preview || preview.totalCount === 0) return;
    setSending(true);
    setError('');
    setConfirmSend(false);
    try {
      const userIds = checkedIds.size > 0 ? Array.from(checkedIds) : undefined;
      const { data } = await api.post<SendResult>('/api/notifications/at-risk/send', { userIds });
      setSendResult(data);
      setPreview(null);
      setPreviewHtml('');
      setCheckedIds(new Set());
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendCount = checkedIds.size > 0 ? checkedIds.size : (preview?.totalCount ?? 0);
  const urgencyColor = (days: number) => days <= 7 ? '#EF4444' : days <= 14 ? '#F59E0B' : '#6B7280';

  return (
    <>
      <p style={{ margin: '0 0 24px', color: '#6B7280', fontSize: 14 }}>
        Send renewal deadline reminders to users with active cycles nearing their end date.
      </p>

      {batches.length > 0 && (
        <div style={{ ...CARD, marginBottom: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Scheduled & Recent Batches</p>
          </div>
          <div>
            {batches.map(b => {
              const scheduledDate = new Date(b.scheduledSendAt).toLocaleString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
              });
              const statusColor = b.status === 'pending' ? '#F59E0B' : b.status === 'sent' ? '#10B981' : '#6B7280';
              return (
                <div key={b._id} style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, background: `${statusColor}18`, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>{b.status}</span>
                  <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>
                    {b.candidates.length} recipient{b.candidates.length !== 1 ? 's' : ''}
                    {b.status === 'pending' && <> · sends <strong>{scheduledDate}</strong></>}
                    {b.status === 'sent' && b.sentCount !== undefined && <> · {b.sentCount} sent, {b.failedCount ?? 0} failed</>}
                    {b.status === 'cancelled' && <> · cancelled</>}
                  </span>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>Created {new Date(b.createdAt).toLocaleDateString()}</span>
                  {b.status === 'pending' && (
                    <button onClick={() => cancelBatch(b._id)} style={{ padding: '4px 12px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Manual Send (Test Mode)
      </p>
      <button onClick={loadPreview} disabled={loading} style={{ ...BTN_PRIMARY, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer', marginBottom: 6 }}>
        {loading ? 'Loading…' : 'Load At-Risk Users'}
      </button>
      <p style={{ margin: '0 0 24px', fontSize: 12, color: '#9CA3AF' }}>
        Preview and send immediately — respects Email Test Mode.
      </p>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#991B1B', fontSize: 14 }}>{error}</div>
      )}

      {sendResult && <SendResultBanner result={sendResult} />}

      {preview && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>At-risk users</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {checkedIds.size > 0 ? `${checkedIds.size} of ${preview.totalCount} selected` : `${preview.totalCount} user${preview.totalCount !== 1 ? 's' : ''} — none selected (will send all)`}
                </p>
              </div>
              {preview.totalCount > 0 && !confirmSend && (
                <button onClick={() => setConfirmSend(true)} style={{ ...BTN_PRIMARY, padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                  Send {checkedIds.size > 0 ? `(${checkedIds.size})` : 'All'}
                </button>
              )}
              {confirmSend && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={sendEmails} disabled={sending} style={{ ...BTN_CONFIRM, cursor: sending ? 'not-allowed' : 'pointer' }}>
                    {sending ? 'Sending…' : `Confirm (${sendCount})`}
                  </button>
                  <button onClick={() => setConfirmSend(false)} style={BTN_CANCEL}>Cancel</button>
                </div>
              )}
            </div>
            {preview.totalCount > 0 && (
              <div onClick={toggleAll} style={{ padding: '8px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" readOnly checked={checkedIds.size === preview.totalCount} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D' }} />
                <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
                  {checkedIds.size === preview.totalCount ? 'Deselect all' : 'Select all'}
                </span>
              </div>
            )}
            {preview.totalCount === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6B7280', fontSize: 14 }}>No at-risk users found.</div>
            ) : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {preview.candidates.map(c => (
                  <div key={c.userId} style={{
                    padding: '12px 16px', borderBottom: '1px solid #F3F4F6',
                    background: selectedUserId === c.userId ? '#EFF6FF' : checkedIds.has(c.userId) ? '#F8FAFF' : 'white',
                    borderLeft: `3px solid ${selectedUserId === c.userId ? '#0F1F3D' : checkedIds.has(c.userId) ? '#93C5FD' : 'transparent'}`,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <input type="checkbox" checked={checkedIds.has(c.userId)}
                      onChange={() => { toggleCheck(c.userId); loadUserPreview(c.userId); }}
                      onClick={e => e.stopPropagation()}
                      style={{ marginTop: 3, width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D', flexShrink: 0 }} />
                    <div onClick={() => loadUserPreview(c.userId)} style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.userName}</p>
                      <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.userEmail}</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#374151', background: '#F3F4F6', padding: '2px 7px', borderRadius: 10 }}>{c.cycleName}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: urgencyColor(c.daysRemaining) }}>{c.daysRemaining}d left</span>
                        <span style={{ fontSize: 11, color: c.isMet ? '#065F46' : '#991B1B', background: c.isMet ? '#D1FAE5' : '#FEE2E2', padding: '2px 7px', borderRadius: 10 }}>
                          {c.isMet ? '✓ Compliant' : `${c.compliancePercentage.toFixed(0)}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Email preview</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                {selectedUserId
                  ? <>Personalized for: <strong>{preview.candidates.find(c => c.userId === selectedUserId)?.userEmail}</strong></>
                  : 'Click or check a user to preview their email'}
              </p>
            </div>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} title="At-Risk Email Preview" style={{ width: '100%', height: 680, border: 'none', display: 'block' }} sandbox="allow-same-origin" />
            ) : (
              <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 14 }}>No email selected</div>
            )}
          </div>
        </div>
      )}

      <AtRiskAutomationPanel />
    </>
  );
}

// ── Profession Alignment Notice tab ──────────────────────────────────────────

interface AlignmentCandidate {
  userId: string;
  userEmail: string;
  userName: string;
  firstName: string;
  professionID: string;
  createdAt: string;
  alreadySent: boolean;
}

interface AlignmentPreview {
  candidates: AlignmentCandidate[];
  sampleHtml: string;
  totalCount: number;
}

function ProfessionAlignmentTab() {
  const [data, setData] = useState<AlignmentPreview | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [error, setError] = useState('');

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setError('');
    setSendResult(null);
    setData(null);
    setPreviewHtml('');
    setSelectedUserId(null);
    setCheckedIds(new Set());
    try {
      const { data: res } = await api.get<AlignmentPreview>('/api/notifications/profession-alignment/candidates');
      setData(res);
      const first = res.candidates.find(c => !c.alreadySent) ?? res.candidates[0];
      if (first) { setSelectedUserId(first.userId); setPreviewHtml(res.sampleHtml); }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount
  React.useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const loadUserPreview = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      const { data: res } = await api.get<{ html: string }>(`/api/notifications/profession-alignment/preview/${userId}`);
      setPreviewHtml(res.html);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleCheck = (userId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data) return;
    const eligible = new Set(data.candidates.filter(c => !c.alreadySent).map(c => c.userId));
    setCheckedIds(prev => prev.size === eligible.size ? new Set() : eligible);
  };

  const sendEmails = async () => {
    if (checkedIds.size === 0) return;
    setSending(true);
    setError('');
    setConfirmSend(false);
    try {
      const { data: res } = await api.post<SendResult>('/api/notifications/profession-alignment/send', { userIds: Array.from(checkedIds) });
      setSendResult(res);
      setCheckedIds(new Set());
      await loadCandidates(); // refresh to show updated ✓ Sent badges
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  const eligibleCount = data ? data.candidates.filter(c => !c.alreadySent).length : 0;

  return (
    <>
      <p style={{ margin: '0 0 24px', color: '#6B7280', fontSize: 14 }}>
        Notify users whose profession was updated by the team to better reflect their actual license credential.
        Users who have already received this notice are shown with a <strong>✓ Sent</strong> badge.
      </p>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#991B1B', fontSize: 14 }}>{error}</div>
      )}

      {sendResult && <SendResultBanner result={sendResult} />}

      {loading && !data && (
        <p style={{ color: '#6B7280', fontSize: 14 }}>Loading recipients…</p>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>LMHC users</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {checkedIds.size > 0 ? `${checkedIds.size} selected` : `${data.totalCount} total · ${eligibleCount} pending`}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={loadCandidates} disabled={loading} style={{ padding: '5px 10px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
                  Refresh
                </button>
                {checkedIds.size > 0 && !confirmSend && (
                  <button onClick={() => setConfirmSend(true)} style={{ ...BTN_PRIMARY, padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                    Send ({checkedIds.size})
                  </button>
                )}
                {confirmSend && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={sendEmails} disabled={sending} style={{ ...BTN_CONFIRM, cursor: sending ? 'not-allowed' : 'pointer' }}>
                      {sending ? 'Sending…' : `Confirm (${checkedIds.size})`}
                    </button>
                    <button onClick={() => setConfirmSend(false)} style={BTN_CANCEL}>Cancel</button>
                  </div>
                )}
              </div>
            </div>
            {eligibleCount > 0 && (
              <div onClick={toggleAll} style={{ padding: '8px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" readOnly checked={checkedIds.size > 0 && checkedIds.size === eligibleCount} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0F1F3D' }} />
                <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
                  {checkedIds.size === eligibleCount ? 'Deselect all' : 'Select all pending'}
                </span>
              </div>
            )}
            {data.totalCount === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6B7280', fontSize: 14 }}>No LMHC users found.</div>
            ) : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {data.candidates.map(c => (
                  <div key={c.userId} style={{
                    padding: '12px 16px', borderBottom: '1px solid #F3F4F6',
                    background: selectedUserId === c.userId ? '#EFF6FF' : checkedIds.has(c.userId) ? '#F8FAFF' : 'white',
                    borderLeft: `3px solid ${selectedUserId === c.userId ? '#0F1F3D' : checkedIds.has(c.userId) ? '#93C5FD' : 'transparent'}`,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    opacity: c.alreadySent ? 0.55 : 1,
                  }}>
                    <input type="checkbox" checked={checkedIds.has(c.userId)} disabled={c.alreadySent}
                      onChange={() => { toggleCheck(c.userId); if (!c.alreadySent) loadUserPreview(c.userId); }}
                      onClick={e => e.stopPropagation()}
                      style={{ marginTop: 3, width: 15, height: 15, cursor: c.alreadySent ? 'not-allowed' : 'pointer', accentColor: '#0F1F3D', flexShrink: 0 }} />
                    <div onClick={() => loadUserPreview(c.userId)} style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.userName}</p>
                      <p style={{ margin: '0 0 4px', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.userEmail}</p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, background: '#EFF6FF', color: '#1D4ED8', padding: '2px 7px', borderRadius: 10 }}>{c.professionID}</span>
                        <span style={{ fontSize: 11, color: '#6B7280' }}>
                          Joined {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {c.alreadySent
                          ? <span style={{ fontSize: 11, background: '#D1FAE5', color: '#065F46', padding: '2px 7px', borderRadius: 10 }}>✓ Sent</span>
                          : <span style={{ fontSize: 11, background: '#FFF7ED', color: '#92400E', padding: '2px 7px', borderRadius: 10 }}>Pending</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={CARD}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F1F3D' }}>Email preview</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                {selectedUserId ? <>Personalized for: <strong>{data.candidates.find(c => c.userId === selectedUserId)?.userEmail}</strong></> : 'Click a user to preview their personalized email'}
              </p>
            </div>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} title="Profession Alignment Email Preview" style={{ width: '100%', height: 680, border: 'none', display: 'block' }} sandbox="allow-same-origin" />
            ) : (
              <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 14 }}>No email selected</div>
            )}
          </div>
        </div>
      )}

      <CronPanel jobType="profession-alignment" showMinDays={false} />
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── CycleCompletionTab ────────────────────────────────────────────────────────

interface TransactionalLog {
  _id: string;
  toEmail: string;
  emailType: string;
  subject: string;
  status: 'sent' | 'failed';
  sentAt: string;
  userId?: { firstName?: string; lastName?: string; email?: string };
}

function CycleCompletionTab() {
  const [logs, setLogs] = useState<TransactionalLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailTypeFilter, setEmailTypeFilter] = useState('cycle_completion');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/notifications/transactional-logs?emailType=${emailTypeFilter}&limit=100`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [emailTypeFilter]);

  React.useEffect(() => { load(); }, [load]);

  const openPreview = async (id: string) => {
    setPreviewId(id);
    setPreviewHtml('');
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/api/notifications/transactional-logs/${id}/html`);
      setPreviewHtml(data.html || '<p style="color:#6B7280;font-family:sans-serif;padding:40px;">No HTML stored for this log entry.</p>');
    } catch {
      setPreviewHtml('<p style="color:#ef4444;font-family:sans-serif;padding:40px;">Failed to load preview.</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const EMAIL_TYPES = [
    { value: 'cycle_completion',    label: 'Cycle Completion' },
    { value: 'deadline_reminder',   label: 'Deadline Reminder' },
    { value: 'onboarding',          label: 'Onboarding' },
    { value: 'cycle_setup',         label: 'Cycle Setup' },
    { value: 'at_risk',             label: 'At-Risk' },
    { value: 'profession_activated',label: 'Profession Activated' },
  ];

  return (
    <>
      {/* Preview modal */}
      {previewId && (
        <div
          onClick={() => setPreviewId(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 12, width: '90vw', maxWidth: 680,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#0F1F3D' }}>Email Preview</span>
              <button
                onClick={() => setPreviewId(null)}
                style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280', lineHeight: 1 }}
              >×</button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {previewLoading ? (
                <p style={{ padding: 40, textAlign: 'center', color: '#6B7280', fontSize: 14 }}>Loading…</p>
              ) : (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  style={{ width: '100%', height: '100%', border: 'none', minHeight: 500 }}
                  sandbox="allow-same-origin"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Email type:</label>
        <select
          value={emailTypeFilter}
          onChange={e => setEmailTypeFilter(e.target.value)}
          style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, background: 'white' }}
        >
          {EMAIL_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button onClick={load} style={{ ...BTN_PRIMARY, padding: '6px 16px', fontSize: 13 }}>Refresh</button>
        <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 'auto' }}>{total} total</span>
      </div>

      <div style={{ ...CARD, overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: 32, textAlign: 'center', color: '#6B7280', fontSize: 14 }}>Loading…</p>
        ) : logs.length === 0 ? (
          <p style={{ padding: 32, textAlign: 'center', color: '#6B7280', fontSize: 14 }}>No emails logged yet for this type.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Sent At</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Recipient</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Subject</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Status</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Preview</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log._id} style={{ borderBottom: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '10px 16px', color: '#374151', whiteSpace: 'nowrap' }}>
                    {new Date(log.sentAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '10px 16px', color: '#374151' }}>
                    <div>{log.toEmail}</div>
                    {log.userId && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                        {[log.userId.firstName, log.userId.lastName].filter(Boolean).join(' ')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', color: '#6B7280', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.subject}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: log.status === 'sent' ? '#D1FAE5' : '#FEE2E2',
                      color: log.status === 'sent' ? '#065F46' : '#991B1B',
                    }}>
                      {log.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => openPreview(log._id)}
                      style={{ ...BTN_PRIMARY, padding: '5px 14px', fontSize: 12, background: '#F3F4F6', color: '#0F1F3D' }}
                    >
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'onboarding' | 'cycle-setup' | 'at-risk' | 'profession-alignment' | 'email-log';

const AdminOnboarding: React.FC = () => {
  usePageTitle('Onboarding Email — Admin');
  const [activeTab, setActiveTab] = useState<Tab>('onboarding');

  const TABS: { id: Tab; label: string }[] = [
    { id: 'onboarding',           label: 'Onboarding Emails' },
    { id: 'cycle-setup',          label: 'Cycle Setup' },
    { id: 'at-risk',              label: 'At-Risk Reminders' },
    { id: 'profession-alignment', label: 'Profession Alignment' },
    { id: 'email-log',            label: 'Email Log' },
  ];

  const PAGE_TITLES: Record<Tab, string> = {
    'onboarding':           'Onboarding Email',
    'cycle-setup':          'Cycle Setup Email',
    'at-risk':              'At-Risk Reminders',
    'profession-alignment': 'Profession Alignment Notice',
    'email-log':            'Transactional Email Log',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fb', padding: '32px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 8, height: 36, background: 'linear-gradient(180deg,#0F1F3D,#0d4a6b)', borderRadius: 4, flexShrink: 0 }} />
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#0F1F3D' }}>
              {PAGE_TITLES[activeTab]}
            </h1>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 28, borderBottom: '2px solid #E5E7EB', paddingBottom: 0 }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px', border: 'none', background: 'transparent',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                color: activeTab === tab.id ? '#0F1F3D' : '#6B7280',
                borderBottom: `2px solid ${activeTab === tab.id ? '#0F1F3D' : 'transparent'}`,
                marginBottom: -2, transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'onboarding' && <OnboardingTab />}
        {activeTab === 'cycle-setup' && <CycleSetupTab />}
        {activeTab === 'at-risk' && <AtRiskTab />}
        {activeTab === 'profession-alignment' && <ProfessionAlignmentTab />}
        {activeTab === 'email-log' && <CycleCompletionTab />}

      </div>
    </div>
  );
};

export default AdminOnboarding;
